import {
  ContextMenuEvent,
  ContextMenuWrapper as ContextMenuWrapperBase,
  useContextMenuEvent as _useContextMenuEvent,
  useContextMenuTrigger as _useContextMenuTrigger,
} from "@deligoo/react-context-menu-wrapper"
import { Button, ThemeColor } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import clsx from "clsx"
import { ComponentProps, CSSProperties, MouseEvent } from "react"

import cls from "./ContextMenu.module.scss"

const WRAPPER_STYLE: CSSProperties = {
  zIndex: 1070, // Tooltip z-index
  pointerEvents: "none",
  transform: "translateY(-50%)",
}

export function createContextMenu<T>(menuId: string) {
  const useContextMenuTrigger = (data: T) => _useContextMenuTrigger({ menuId, data })
  const useContextMenuEvent = () => _useContextMenuEvent<T>() as ContextMenuEvent<T>
  const ContextMenuWrapper = ({ children }: React.ComponentProps<typeof ContextMenuWrapperBase>) => (
    <ContextMenuWrapperBase id={menuId} wrapperStyle={WRAPPER_STYLE}>
      {children}
    </ContextMenuWrapperBase>
  )

  return { useContextMenuTrigger, useContextMenuEvent, ContextMenuWrapper }
}

export function openContextMenu(e: MouseEvent<HTMLButtonElement>) {
  e.target.dispatchEvent(
    new window.MouseEvent("contextmenu", {
      bubbles: true,
      clientX: e.clientX,
      clientY: e.clientY,
    })
  )
  e.stopPropagation()
}

type ContextMenuProps = {
  children: React.ReactNode
  className?: string
}

export const ContextMenu = ({ className, children }: ContextMenuProps) => {
  return (
    <div className={cls.wrapper}>
      <ul className={clsx(cls.contextMenu, className)}>
        <li className={cls.item}>{children}</li>
      </ul>
    </div>
  )
}

type ItemProps = ComponentProps<typeof Button> & {
  iconName: IconName
  iconColor?: ThemeColor
  children: React.ReactNode
}

const Item = ({ iconName, iconColor = "primary", children, disabled, ...props }: ItemProps) => {
  return (
    <Button
      color="dark"
      variation="text"
      iconSide="right"
      iconProps={{ name: iconName, color: disabled ? "muted" : iconColor }}
      className={cls.itemButton}
      disabled={disabled}
      fullWidth
      {...props}
    >
      {children}
    </Button>
  )
}

type MenuButtonProps = ComponentProps<typeof Button> & { vertical?: boolean }

const MenuButton = ({ onClick, className, vertical = true, ...props }: MenuButtonProps) => {
  return (
    <Button
      size="small"
      variation="contrast"
      iconProps={{ name: vertical ? "dots-vertical" : "dots-horizontal" }}
      className={clsx(cls.menuButton, className)}
      {...props}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        onClick(e)
      }}
    />
  )
}

ContextMenu.Item = Item
ContextMenu.Button = MenuButton
